import { NavLink, useLocation, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { ZelofinLogo } from "./ZelofinLogo";
import {
  SidebarFooter,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { OrganizationSelector } from "./OrganizationSelector";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Bell,
  BarChart3,
  CreditCard,
  Target,
  TrendingDown,
  TrendingUp,
  Tags,
  Heart,
  Wallet,
  ChevronDown,
  Settings,
  ArrowDownToLine,
  Users,
  Calendar,
  WalletCards,
  PiggyBank,
  BarChart2,
  CalendarCheck,
  CalendarClock,
  X,
  Upload,
} from "lucide-react";

interface MenuItem {
  title: string;
  url?: string;
  icon: any;
  subItems?: MenuItem[];
  isNew?: boolean;
}

const menuItems: MenuItem[] = [
  { title: "Resumo", url: "/", icon: BarChart3 },
  { title: "Lançamentos", url: "/transactions", icon: TrendingUp },
  { title: "Importar", url: "/imports", icon: Upload, isNew: true },
  { title: "Contas", url: "/accounts", icon: Wallet },
  { title: "Cofrinhos", url: "/cofrinhos", icon: PiggyBank },
  { title: "Agenda", url: "/calendar", icon: CalendarCheck, isNew: true },
  { title: "Calendário", url: "/routine-calendar", icon: CalendarClock, isNew: true },
  { 
    title: "Cartões", 
    icon: CreditCard,
    subItems: [
      { title: "Minha Carteira", url: "/cards/walletcards", icon: WalletCards },
      { title: "Gastos", url: "/cards/expenses", icon: ArrowDownToLine },
      { title: "Faturas", url: "/cards/invoices", icon: Calendar },
      { title: "Relatórios", url: "/cards/reports", icon: BarChart2 },
    ]
  },
  { title: "Categorias", url: "/categories", icon: Tags },
  { title: "Compromissos", url: "/commitments", icon: Heart },
  { title: "Funcionários", url: "/employees", icon: Users },
  { title: "Dívidas", url: "/debts", icon: TrendingDown },
  { title: "Metas", url: "/goals", icon: Target },
  {
    title: "Configurações",
    icon: Settings,
    subItems: [
      { title: "Geral", url: "/settings", icon: Settings },
      { title: "Usuários", url: "/settings/users", icon: Users },
    ]
  },
];

export function AppSidebar() {
  const { open, isMobile, setOpenMobile } = useSidebar();
  // On mobile the sidebar renders as a Sheet (drawer) — always show expanded layout.
  const isExpanded = open || isMobile;

  const closeMobileSheet = () => {
    if (isMobile) setOpenMobile(false);
  };
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const currentPath = location.pathname;
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const [openPopover, setOpenPopover] = useState<string | null>(null);

  const displayName =
    profile.full_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Usuário';

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Função auxiliar para construir URL com slug da organização
  const buildOrgUrl = (path: string) => {
    const slug = orgSlug || currentOrganization?.slug;
    if (!slug) return path;
    return `/org/${slug}${path}`;
  };

  // Efeito para abrir automaticamente submenus que contêm a rota ativa
  useEffect(() => {
    const activeSubmenus: Record<string, boolean> = {};
    menuItems.forEach(item => {
      if (item.subItems) {
        const hasActiveChild = item.subItems.some(subItem => 
          subItem.url && isActive(buildOrgUrl(subItem.url))
        );
        if (hasActiveChild) {
          activeSubmenus[item.title] = true;
        }
      }
    });
    
    setOpenSubmenus(prev => ({ ...prev, ...activeSubmenus }));
  }, [currentPath, orgSlug, currentOrganization]);

  const toggleSubmenu = (itemTitle: string) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [itemTitle]: !prev[itemTitle]
    }));
  };

  const isActive = (path: string) => {
    // Remover /org/:slug da comparação
    const pathWithoutOrg = currentPath.replace(/^\/org\/[^/]+/, '');
    const targetPath = path.replace(/^\/org\/[^/]+/, '');
    
    if (targetPath === "/" && pathWithoutOrg === "/") return true;
    if (targetPath === "/" && pathWithoutOrg === "") return true;
    return targetPath !== "/" && pathWithoutOrg.startsWith(targetPath);
  };

  const hasActiveSubItem = (item: MenuItem) => {
    if (!item.subItems) return false;
    return item.subItems.some(subItem => subItem.url && isActive(subItem.url));
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isSubmenuOpen = openSubmenus[item.title];
    const parentActive = hasActiveSubItem(item);
    
    if (hasSubItems) {
      // Sidebar recolhida: submenu flutuante
      if (!isExpanded) {
        return (
          <SidebarMenuItem key={item.title}>
            <Popover open={openPopover === item.title} onOpenChange={(open) => setOpenPopover(open ? item.title : null)}>
              <PopoverTrigger asChild>
                <SidebarMenuButton isActive={parentActive}>
                  <item.icon className="h-4 w-4" />
                </SidebarMenuButton>
              </PopoverTrigger>
              <PopoverContent side="right" align="start" sideOffset={12} className="w-44 p-1.5">
                <p className="mb-1 px-2 py-1 text-xs font-semibold text-muted-foreground">{item.title}</p>
                {item.subItems!.map((subItem) => (
                  <NavLink
                    key={subItem.title}
                    to={buildOrgUrl(subItem.url || "#")}
                    end={subItem.url === "/"}
                    onClick={() => setOpenPopover(null)}
                    className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                      subItem.url && isActive(buildOrgUrl(subItem.url))
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <subItem.icon className="h-4 w-4" />
                    <span>{subItem.title}</span>
                  </NavLink>
                ))}
              </PopoverContent>
            </Popover>
          </SidebarMenuItem>
        );
      }

      // Sidebar expandida: Collapsible normal
      return (
        <Collapsible key={item.title} open={isSubmenuOpen} onOpenChange={() => toggleSubmenu(item.title)}>
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton isActive={parentActive} tooltip={item.title}>
                <item.icon className="h-4 w-4" />
                {isExpanded && (
                  <>
                    <span>{item.title}</span>
                    <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`} />
                  </>
                )}
              </SidebarMenuButton>
            </CollapsibleTrigger>
            {isExpanded && (
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.subItems!.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.title}>
                      <SidebarMenuSubButton asChild isActive={subItem.url ? isActive(subItem.url) : false}>
                        <NavLink to={buildOrgUrl(subItem.url || "#")} end={subItem.url === "/"} onClick={closeMobileSheet}>
                          <subItem.icon className="h-4 w-4" />
                          <span>{subItem.title}</span>
                        </NavLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            )}
          </SidebarMenuItem>
        </Collapsible>
      );
    } else {
      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild isActive={isActive(item.url!)} tooltip={item.title}>
            <NavLink
              to={buildOrgUrl(item.url!)}
              end={item.url === "/"}
              onClick={closeMobileSheet}
            >
              <item.icon className="h-4 w-4" />
              {isExpanded && (
                <span className="flex items-center gap-2">
                  {item.title}
                  {item.isNew && (
                    <span className="rounded-full bg-sidebar-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-sidebar-primary">
                      new
                    </span>
                  )}
                </span>
              )}
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }
  };

  return (
    <Sidebar collapsible="icon" className="bg-transparent">
      <SidebarContent className="gap-0 scrollbar-sidebar">
        <SidebarHeader className="px-4 pb-3 pt-5">
          <div className={`flex items-center ${isExpanded ? 'gap-3' : 'justify-center'}`}>
            {isExpanded ? (
              <ZelofinLogo variant="icon-text" forceDark className="h-9 w-auto" />
            ) : (
              <ZelofinLogo variant="icon" className="h-10 w-10 rounded-[18px] object-contain" />
            )}
            {isMobile && (
              <button
                type="button"
                onClick={closeMobileSheet}
                aria-label="Fechar menu"
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-[12px] text-sidebar-foreground/60 transition-colors hover:bg-white/10 hover:text-sidebar-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </SidebarHeader>

        {isExpanded && <div className="px-4 pb-4"><OrganizationSelector /></div>}

        <SidebarSeparator className="mx-4 bg-white/8" />

        <SidebarGroup>
          <SidebarGroupLabel>Menu Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1.5">
              {menuItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="mt-auto px-3 pb-4 pt-2">
        <div className="flex flex-col gap-3">
          <button
            type="button"
            aria-label="Notificacoes"
            className={`relative flex h-11 items-center rounded-[18px] border border-white/10 bg-white/5 text-sidebar-foreground/80 transition-all duration-200 ease-premium hover:bg-white/10 ${isExpanded ? 'justify-start gap-3 px-3' : 'justify-center'}`}
          >
            <Bell className="h-4 w-4" />
            {isExpanded && <span className="text-sm font-medium">Notificacoes</span>}
            <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-warning" />
          </button>

          <div className={`flex items-center rounded-[20px] border border-white/10 bg-white/5 text-sidebar-foreground ${isExpanded ? 'gap-3 px-3 py-3' : 'justify-center p-2'}`}>
            <Avatar className="h-11 w-11 border border-white/10 bg-sidebar-primary/20">
              <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground font-semibold">
                {getInitials(displayName)}
              </AvatarFallback>
            </Avatar>
            {isExpanded && (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-sidebar-foreground">
                  {displayName}
                </p>
                <p className="truncate text-xs text-sidebar-foreground/55">{user?.email}</p>
              </div>
            )}
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}